import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenantSafe } from '../context/TenantContext';
import RichTextEditor from '../components/RichTextEditor';
import {
  getPosts,
  getPostById,
  getTags,
  getAdminPosts,
  createPost,
  updatePost,
  deletePost,
  toggleLike,
  addComment,
} from '../services/communityNewsService';

// ─── Styles ──────────────────────────────────────────────────────────────────
const S = {
  page:    { padding: '28px 32px', maxWidth: 1100, margin: '0 auto' },
  heading: { fontSize: 24, fontWeight: 700, marginBottom: 4 },
  sub:     { color: '#6b7280', fontSize: 14, marginBottom: 24 },
  tabs:    { display: 'flex', gap: 8, marginBottom: 24 },
  tab: (active) => ({
    padding: '8px 18px', borderRadius: 6, fontWeight: 600, fontSize: 14,
    border: active ? '2px solid #166534' : '2px solid #e5e7eb',
    background: active ? '#f0fdf4' : '#fff',
    color: active ? '#166534' : '#6b7280',
    cursor: 'pointer',
  }),
  card: {
    background: '#fff', border: '1px solid #e5e7eb',
    borderRadius: 10, padding: '20px 22px', marginBottom: 14,
  },
  btn: (bg = '#166534', fg = '#fff') => ({
    padding: '7px 16px', background: bg, color: fg,
    border: 'none', borderRadius: 6, cursor: 'pointer',
    fontSize: 13, fontWeight: 600,
  }),
  err: {
    background: '#fef2f2', border: '1px solid #fca5a5',
    borderRadius: 6, padding: '10px 14px', color: '#991b1b', fontSize: 14, marginBottom: 14,
  },
  ok: {
    background: '#f0fdf4', border: '1px solid #86efac',
    borderRadius: 6, padding: '10px 14px', color: '#166534', fontSize: 14, marginBottom: 14,
  },
  input: {
    width: '100%', padding: '8px 12px', border: '1px solid #d1d5db',
    borderRadius: 6, fontSize: 14, boxSizing: 'border-box',
  },
  label: { fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' },
  field: { marginBottom: 16 },
  chip: (active) => ({
    padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
    background: active ? '#166534' : '#f3f4f6',
    color: active ? '#fff' : '#374151',
    border: 'none', cursor: 'pointer',
  }),
};

const PRIORITY_COLOR = {
  urgent: '#ef4444', high: '#f59e0b', normal: '#22c55e', low: '#94a3b8',
};

function PriorityBadge({ priority }) {
  const c = PRIORITY_COLOR[priority] || '#94a3b8';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 10,
      fontSize: 11, fontWeight: 700, background: c + '22', color: c,
    }}>
      {priority?.toUpperCase()}
    </span>
  );
}

function formatDate(d) {
  return new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
}

// ─── PostCard ─────────────────────────────────────────────────────────────────
function PostCard({ post, userId, onOpen, onLike }) {
  const liked = post.likes?.some((l) => l.userId === userId || l.userId?._id === userId);
  return (
    <div style={S.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <h3
            style={{ fontSize: 18, fontWeight: 700, cursor: 'pointer', color: '#111827', margin: '0 0 6px' }}
            onClick={() => onOpen(post._id)}
          >
            {post.title}
          </h3>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
            <PriorityBadge priority={post.priority} />
            <span style={{ color: '#6b7280', fontSize: 13 }}>{formatDate(post.publishDate)}</span>
            {post.author?.name && <span style={{ color: '#9ca3af', fontSize: 12 }}>by {post.author.name}</span>}
          </div>
        </div>
      </div>

      <p
        style={{ color: '#374151', fontSize: 14, lineHeight: 1.6, marginBottom: 12 }}
        dangerouslySetInnerHTML={{ __html: post.excerpt || '' }}
      />

      {post.tags?.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {post.tags.map((t) => (
            <span key={t} style={{
              padding: '2px 10px', background: '#f0fdf4', color: '#166534',
              borderRadius: 12, fontSize: 12, fontWeight: 500,
            }}>
              {t}
            </span>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, color: '#6b7280' }}>
        <span>👁️ {post.viewCount}</span>
        <span>💬 {post.comments?.length || 0}</span>
        <button
          onClick={() => onLike(post._id)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: liked ? '#ef4444' : '#9ca3af' }}
        >
          {liked ? '❤️' : '🤍'} {post.likes?.length || 0}
        </button>
        <div style={{ flex: 1 }} />
        <button style={S.btn('#f3f4f6', '#374151')} onClick={() => onOpen(post._id)}>
          Read more →
        </button>
      </div>
    </div>
  );
}

// ─── PostDetail ───────────────────────────────────────────────────────────────
function PostDetail({ post, userId, onBack, onLike, onComment }) {
  const [comment, setComment]     = useState('');
  const [submitting, setSubmit]   = useState(false);
  const liked = post.likes?.some((l) => l.userId === userId || l.userId?._id === userId);

  const handleComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setSubmit(true);
    await onComment(post._id, comment);
    setComment('');
    setSubmit(false);
  };

  return (
    <div>
      <button style={{ ...S.btn('#f3f4f6', '#374151'), marginBottom: 20 }} onClick={onBack}>
        ← Back to News
      </button>

      <article style={S.card}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
          <PriorityBadge priority={post.priority} />
          <span style={{ color: '#6b7280', fontSize: 13 }}>{formatDate(post.publishDate)}</span>
          {post.author?.name && <span style={{ color: '#9ca3af', fontSize: 12 }}>by {post.author.name}</span>}
          <span style={{ color: '#9ca3af', fontSize: 12 }}>👁️ {post.viewCount} views</span>
        </div>

        <h1 style={{ fontSize: 26, fontWeight: 700, marginBottom: 16, color: '#111827' }}>{post.title}</h1>

        <div
          style={{ fontSize: 15, lineHeight: 1.75, color: '#374151' }}
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {post.tags?.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 20 }}>
            {post.tags.map((t) => (
              <span key={t} style={{
                padding: '2px 10px', background: '#f0fdf4', color: '#166534',
                borderRadius: 12, fontSize: 12,
              }}>
                {t}
              </span>
            ))}
          </div>
        )}

        <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 20, paddingTop: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            onClick={() => onLike(post._id)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: liked ? '#ef4444' : '#9ca3af' }}
          >
            {liked ? '❤️' : '🤍'} {post.likes?.length || 0} likes
          </button>
        </div>

        {/* Comments */}
        <div style={{ marginTop: 24 }}>
          <h3 style={{ fontWeight: 700, marginBottom: 14 }}>Comments ({post.comments?.length || 0})</h3>
          {post.comments?.map((c, i) => (
            <div key={i} style={{ borderLeft: '3px solid #e5e7eb', paddingLeft: 12, marginBottom: 12 }}>
              <span style={{ fontWeight: 600, fontSize: 13 }}>{c.userName}</span>
              <span style={{ color: '#9ca3af', fontSize: 11, marginLeft: 8 }}>{formatDate(c.createdAt)}</span>
              <p style={{ margin: '4px 0 0', fontSize: 14, color: '#374151' }}>{c.content}</p>
            </div>
          ))}
          {userId && (
            <form onSubmit={handleComment} style={{ marginTop: 16 }}>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add a comment…"
                rows={3}
                style={{ ...S.input, resize: 'vertical', marginBottom: 8 }}
              />
              <button type="submit" style={S.btn()} disabled={submitting || !comment.trim()}>
                {submitting ? 'Posting…' : 'Post Comment'}
              </button>
            </form>
          )}
        </div>
      </article>
    </div>
  );
}

// ─── NewsFeed ─────────────────────────────────────────────────────────────────
function NewsFeed({ tenantId, userId }) {
  const [posts, setPosts]           = useState([]);
  const [tags, setTags]             = useState([]);
  const [selectedTags, setSelTags]  = useState([]);
  const [search, setSearch]         = useState('');
  const [priority, setPriority]     = useState('');
  const [sortBy, setSortBy]         = useState('publishDate');
  const [sortOrder, setSortOrder]   = useState('desc');
  const [page, setPage]             = useState(1);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [detailPost, setDetailPost] = useState(null);
  const [detailLoading, setDetailLd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [postsRes, tagsRes] = await Promise.all([
        getPosts(tenantId, {
          page, limit: 10,
          tags:     selectedTags.join(',') || undefined,
          search:   search   || undefined,
          priority: priority || undefined,
          sortBy, sortOrder,
        }),
        getTags(tenantId),
      ]);
      setPosts(postsRes.data.posts);
      setPagination(postsRes.data.pagination);
      setTags(tagsRes.data.tags);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load news');
    } finally {
      setLoading(false);
    }
  }, [tenantId, page, selectedTags, search, priority, sortBy, sortOrder]);

  useEffect(() => { load(); }, [load]);

  const openPost = async (id) => {
    setDetailLd(true);
    try {
      const res = await getPostById(tenantId, id);
      setDetailPost(res.data.post);
    } finally {
      setDetailLd(false);
    }
  };

  const handleLike = async (id) => {
    await toggleLike(tenantId, id);
    if (detailPost?._id === id) {
      const res = await getPostById(tenantId, id);
      setDetailPost(res.data.post);
    } else {
      await load();
    }
  };

  const handleComment = async (id, content) => {
    await addComment(tenantId, id, content);
    const res = await getPostById(tenantId, id);
    setDetailPost(res.data.post);
  };

  if (detailLoading) return <p>Loading post…</p>;

  if (detailPost) {
    return (
      <PostDetail
        post={detailPost}
        userId={userId}
        onBack={() => setDetailPost(null)}
        onLike={handleLike}
        onComment={handleComment}
      />
    );
  }

  return (
    <div>
      {error && <div style={S.err}>{error}</div>}

      {/* Controls */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search news…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          style={{ ...S.input, maxWidth: 260, marginBottom: 0 }}
        />
        <select
          value={priority}
          onChange={(e) => { setPriority(e.target.value); setPage(1); }}
          style={{ ...S.input, width: 'auto', marginBottom: 0 }}
        >
          <option value="">All priorities</option>
          <option value="urgent">🔴 Urgent</option>
          <option value="high">🟡 High</option>
          <option value="normal">🟢 Normal</option>
          <option value="low">🔵 Low</option>
        </select>
        <select
          value={`${sortBy}-${sortOrder}`}
          onChange={(e) => {
            const [f, o] = e.target.value.split('-');
            setSortBy(f); setSortOrder(o); setPage(1);
          }}
          style={{ ...S.input, width: 'auto', marginBottom: 0 }}
        >
          <option value="publishDate-desc">📅 Newest first</option>
          <option value="publishDate-asc">📅 Oldest first</option>
          <option value="priority-desc">⚡ Priority high→low</option>
          <option value="title-asc">🔤 Title A–Z</option>
        </select>
      </div>

      {/* Tag chips */}
      {tags.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
          {tags.map((t) => (
            <button
              key={t}
              style={S.chip(selectedTags.includes(t))}
              onClick={() => {
                setSelTags((prev) =>
                  prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]
                );
                setPage(1);
              }}
            >
              {t}
            </button>
          ))}
          {selectedTags.length > 0 && (
            <button style={S.chip(false)} onClick={() => { setSelTags([]); setPage(1); }}>
              × clear
            </button>
          )}
        </div>
      )}

      {loading
        ? <p>Loading…</p>
        : posts.length === 0
          ? <p style={{ color: '#6b7280' }}>No news posts yet.</p>
          : posts.map((p) => (
              <PostCard key={p._id} post={p} userId={userId} onOpen={openPost} onLike={handleLike} />
            ))
      }

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 20 }}>
          <button style={S.btn('#f3f4f6', '#374151')} disabled={!pagination.hasPrev} onClick={() => setPage((p) => p - 1)}>
            ← Prev
          </button>
          <span style={{ fontSize: 13, color: '#6b7280' }}>
            Page {pagination.currentPage} of {pagination.totalPages}
          </span>
          <button style={S.btn('#f3f4f6', '#374151')} disabled={!pagination.hasNext} onClick={() => setPage((p) => p + 1)}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

// ─── PostForm ─────────────────────────────────────────────────────────────────
function PostForm({ tenantId, initial, onSave, onCancel }) {
  const [form, setForm] = useState({
    title:       initial?.title       || '',
    content:     initial?.content     || '',
    tags:        initial?.tags?.join(', ') || '',
    status:      initial?.status      || 'draft',
    priority:    initial?.priority    || 'normal',
    publishDate: initial?.publishDate ? initial.publishDate.slice(0, 10) : new Date().toISOString().slice(0, 10),
    expiryDate:  initial?.expiryDate  ? initial.expiryDate.slice(0, 10) : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      setError('Title and content are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        expiryDate: form.expiryDate || null,
      };
      if (initial?._id) {
        await updatePost(tenantId, initial._id, payload);
      } else {
        await createPost(tenantId, payload);
      }
      onSave();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={S.card}>
      <h3 style={{ fontWeight: 700, marginBottom: 16 }}>
        {initial?._id ? 'Edit Post' : 'New Post'}
      </h3>
      {error && <div style={S.err}>{error}</div>}

      <div style={S.field}>
        <label style={S.label}>Title *</label>
        <input style={S.input} value={form.title} onChange={(e) => set('title', e.target.value)} maxLength={200} required />
      </div>

      <div style={S.field}>
        <label style={S.label}>Content *</label>
        <RichTextEditor
          value={form.content}
          onChange={(v) => set('content', v)}
          placeholder="Write your news post here…"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={S.label}>Status</label>
          <select style={S.input} value={form.status} onChange={(e) => set('status', e.target.value)}>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        <div>
          <label style={S.label}>Priority</label>
          <select style={S.input} value={form.priority} onChange={(e) => set('priority', e.target.value)}>
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
        <div>
          <label style={S.label}>Publish Date</label>
          <input type="date" style={S.input} value={form.publishDate} onChange={(e) => set('publishDate', e.target.value)} />
        </div>
        <div>
          <label style={S.label}>Expiry Date (optional)</label>
          <input type="date" style={S.input} value={form.expiryDate} onChange={(e) => set('expiryDate', e.target.value)} />
        </div>
      </div>

      <div style={S.field}>
        <label style={S.label}>Tags (comma-separated)</label>
        <input
          style={S.input}
          value={form.tags}
          onChange={(e) => set('tags', e.target.value)}
          placeholder="announcements, events, updates"
        />
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button type="submit" style={S.btn()} disabled={saving}>
          {saving ? 'Saving…' : initial?._id ? 'Update Post' : 'Create Post'}
        </button>
        <button type="button" style={S.btn('#f3f4f6', '#374151')} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── ManageView ───────────────────────────────────────────────────────────────
function ManageView({ tenantId }) {
  const [posts, setPosts]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const [editing, setEditing]       = useState(null);
  const [statusFilter, setStatus]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminPosts(tenantId, { limit: 50, status: statusFilter || undefined });
      setPosts(res.data.posts);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, [tenantId, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setEditing(null);
    setSuccess('Post saved.');
    await load();
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    try {
      await deletePost(tenantId, id);
      setSuccess('Post deleted.');
      await load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to delete');
    }
  };

  if (editing) {
    return (
      <PostForm
        tenantId={tenantId}
        initial={editing === 'new' ? null : editing}
        onSave={handleSave}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div>
      {error   && <div style={S.err}>{error}</div>}
      {success && <div style={S.ok}>{success}</div>}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <button style={S.btn()} onClick={() => setEditing('new')}>+ New Post</button>
        <select
          style={{ ...S.input, width: 'auto', marginBottom: 0 }}
          value={statusFilter}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {loading
        ? <p>Loading…</p>
        : posts.length === 0
          ? <p style={{ color: '#6b7280' }}>No posts yet. Click "+ New Post" to create one.</p>
          : posts.map((p) => (
              <div key={p._id} style={{ ...S.card, display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 700 }}>{p.title}</span>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                    <PriorityBadge priority={p.priority} />
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                      background: p.status === 'published' ? '#f0fdf4' : p.status === 'draft' ? '#fefce8' : '#f3f4f6',
                      color:      p.status === 'published' ? '#166534' : p.status === 'draft' ? '#92400e' : '#6b7280',
                    }}>
                      {p.status}
                    </span>
                    <span style={{ fontSize: 12, color: '#9ca3af' }}>{formatDate(p.publishDate)}</span>
                    <span style={{ fontSize: 12, color: '#9ca3af' }}>
                      👁️ {p.viewCount} · 💬 {p.comments?.length || 0} · ❤️ {p.likes?.length || 0}
                    </span>
                  </div>
                  {p.tags?.length > 0 && (
                    <div style={{ marginTop: 4, fontSize: 11, color: '#6b7280' }}>{p.tags.join(', ')}</div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button style={S.btn('#1d4ed8')} onClick={() => setEditing(p)}>Edit</button>
                  <button style={S.btn('#ef4444')} onClick={() => handleDelete(p._id)}>Delete</button>
                </div>
              </div>
            ))
      }
    </div>
  );
}

// ─── CommunityNewsPage ────────────────────────────────────────────────────────
const CommunityNewsPage = () => {
  const { user, activeTenant, activeMembership } = useAuth();
  const tenantCtx = useTenantSafe();
  const tenantId = tenantCtx?.viewTenant?._id || activeTenant?._id;
  const viewerRole = tenantCtx?.viewerRole || activeMembership?.communityRole;
  const isManager =
    tenantCtx?.isPlatformStaff ||
    ['cip_admin', 'cip_staff'].includes(user?.globalRole) ||
    ['community_admin', 'community_staff'].includes(viewerRole);
  const [tab, setTab] = useState('feed');

  if (!tenantId) {
    return (
      <div style={S.page}>
        <h1 style={S.heading}>Community News</h1>
        <p style={{ color: '#6b7280' }}>No active community. Join a community to read its news.</p>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <h1 style={S.heading}>📰 Community News</h1>
      <p style={S.sub}>Announcements and updates from {activeTenant?.name || 'your community'}</p>

      {isManager && (
        <div style={S.tabs}>
          <button style={S.tab(tab === 'feed')}   onClick={() => setTab('feed')}>News Feed</button>
          <button style={S.tab(tab === 'manage')} onClick={() => setTab('manage')}>Manage Posts</button>
        </div>
      )}

      {tab === 'feed'   && <NewsFeed   tenantId={tenantId} userId={user?._id} />}
      {tab === 'manage' && <ManageView tenantId={tenantId} />}
    </div>
  );
};

export default CommunityNewsPage;
